import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import PillarPageLayout from "./PillarPageLayout";
import AuthorByline from "@/components/AuthorByline";

const FAQ = [
  {
    q: "¿Cómo sé si tengo el sistema inmunológico débil?",
    a: "Señales típicas: te enfermas más de 3 veces al año (gripes, infecciones), las heridas tardan en cicatrizar, tienes fatiga crónica, alergias frecuentes, problemas digestivos recurrentes o reactivaciones de virus latentes (herpes labial, varicela zóster).",
  },
  {
    q: "¿Cuánto tarda en fortalecerse el sistema inmune?",
    a: "Las primeras señales (mejor energía, menos fatiga) se notan en 2-4 semanas con buenos hábitos. Cambios estructurales sostenibles tardan 3-6 meses. La microbiota intestinal — base del 70% de tu sistema inmune — necesita al menos 8-12 semanas para reequilibrarse.",
  },
  {
    q: "¿Qué nutrientes son críticos para la inmunidad?",
    a: "Los más estudiados: vitamina D3 (con K2), vitamina C, zinc, selenio, glutatión (o sus precursores como Immunocal), omega-3 EPA/DHA y probióticos. La deficiencia de cualquiera de estos compromete la respuesta inmune.",
  },
  {
    q: "¿El estrés afecta realmente al sistema inmune?",
    a: "Sí, profundamente. El cortisol crónicamente elevado suprime los linfocitos T y B, reduce las células NK (asesinas naturales) y aumenta la inflamación de bajo grado. Manejar el estrés (sueño, meditación, naturaleza) es tan importante como suplementar.",
  },
  {
    q: "¿Vale la pena tomar suplementos si tengo una buena alimentación?",
    a: "En el contexto moderno (suelos empobrecidos, alimentos procesados, estrés constante), incluso una dieta excelente puede ser insuficiente. La suplementación dirigida — basada en evaluación de tus síntomas, hábitos y deficiencias — actúa como respaldo estratégico.",
  },
];

const RELATED = [
  { label: "Immunocal: el precursor de glutatión", href: "/immunocal", desc: "La proteína más estudiada para apoyar al sistema inmune." },
  { label: "Glutatión: el antioxidante maestro", href: "/glutation", desc: "Por qué tus defensas dependen de mantener glutatión alto." },
  { label: "Blog Eliora", href: "/blog", desc: "Biohacking, medicina funcional y prevención inteligente." },
];

export default function SistemaInmunologicoPage(props) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <Helmet>
        <title>Cómo fortalecer tu sistema inmunológico: guía completa 2026</title>
        <meta name="description" content="Guía científica para fortalecer tu sistema inmunológico: alimentación, suplementación, sueño, manejo del estrés y prevención desde la medicina funcional." />
        <link rel="canonical" href="https://elioralongevity.com/sistema-inmunologico" />
        <meta property="og:title" content="Cómo fortalecer tu sistema inmunológico: guía completa 2026" />
        <meta property="og:description" content="Fortalece tus defensas con estrategias de medicina funcional, suplementación dirigida y hábitos respaldados por ciencia." />
        <meta property="og:url" content="https://elioralongevity.com/sistema-inmunologico" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        })}</script>
      </Helmet>

      <PillarPageLayout
        {...props}
        meta={{
          eyebrow: "Inmunidad · Medicina funcional",
          h1: "Cómo fortalecer tu sistema inmunológico",
          lead: "Una inmunidad fuerte no es genética: es el resultado diario de lo que comes, cómo duermes, cómo manejas el estrés y los nutrientes que aportas a tus células. Esta guía une ciencia y práctica.",
        }}
        breadcrumbs={[{ label: "Inicio", href: "/" }, { label: "Sistema inmunológico" }]}
        faq={FAQ}
        relatedLinks={RELATED}
      >
        <AuthorByline date="2026-05-30" minutes={10} />

        <h2 className="font-serif text-3xl mt-2 mb-4">Por qué tu sistema inmune importa hoy más que nunca</h2>
        <p className="text-[#1a2421] leading-relaxed mb-8">
          La pandemia nos enseñó algo: tener un sistema inmunológico fuerte no es opcional. No se trata de "no enfermarse" — se trata de cómo tu cuerpo <strong>responde, contiene y se recupera</strong> cuando un patógeno entra. Y esa capacidad se construye con decisiones diarias.
        </p>

        <h2 className="font-serif text-3xl mt-10 mb-4">Los 5 pilares de la inmunidad fuerte</h2>
        <p className="text-[#1a2421] leading-relaxed mb-4">Desde el enfoque de la <strong>medicina funcional</strong>:</p>
        <ol className="list-decimal pl-6 space-y-3 mb-8 text-[#1a2421]">
          <li><strong>Microbiota intestinal saludable</strong> — el 70% de tus células inmunes viven en el intestino. Apoyar la microbiota es apoyar al sistema inmune.</li>
          <li><strong>Sueño profundo y consistente</strong> — durante el sueño se producen citocinas inmunorreguladoras. Menos de 6 horas/noche reduce la respuesta a vacunas hasta 50%.</li>
          <li><strong>Nutrientes específicos</strong> — vitamina D, zinc, glutatión, omega-3, selenio.</li>
          <li><strong>Control del estrés crónico</strong> — el cortisol sostenido suprime la inmunidad.</li>
          <li><strong>Movimiento moderado y regular</strong> — el ejercicio aumenta la inmunovigilancia (las células NK patrullan más).</li>
        </ol>

        <h2 className="font-serif text-3xl mt-10 mb-4">El rol del glutatión en la inmunidad</h2>
        <p className="text-[#1a2421] leading-relaxed mb-8">
          Los <strong>linfocitos T</strong> — la espada del sistema inmune adaptativo — requieren glutatión para proliferar y matar células infectadas. Cuando los niveles de <a href="/glutation" className="text-[#bc6c25] font-semibold underline underline-offset-4">glutatión intracelular</a> caen, la respuesta inmune se debilita. Por eso elevar el glutatión mediante <a href="/immunocal" className="text-[#bc6c25] font-semibold underline underline-offset-4">precursores como Immunocal</a> es una estrategia central en protocolos de fortalecimiento inmune.
        </p>

        <h2 className="font-serif text-3xl mt-10 mb-4">Hábitos prácticos: empieza esta semana</h2>
        <ul className="list-disc pl-6 space-y-2 mb-8 text-[#1a2421]">
          <li><strong>Dormir 7-9 horas</strong> en horario consistente (a oscuras totales y sin pantallas 60 min antes).</li>
          <li><strong>30g+ de fibra fermentable al día</strong> (verduras, legumbres, avena, chía) para alimentar a la microbiota.</li>
          <li><strong>Exposición al sol matutino</strong> 10-15 min/día para vitamina D y regulación circadiana.</li>
          <li><strong>Movimiento moderado</strong> 30 min, 5 días/semana (caminar enérgico, yoga, fuerza ligera).</li>
          <li><strong>Hidratación</strong> 30 ml/kg de peso/día con agua filtrada.</li>
          <li><strong>Reducir azúcar refinada</strong>: cada 100g de azúcar reduce la fagocitosis (capacidad de las células inmunes de "comer" patógenos) hasta 50% por 5 horas.</li>
          <li><strong>Práctica de estrés</strong>: respiración 4-7-8, naturaleza, oración, meditación — 10 min/día.</li>
        </ul>

        <h2 className="font-serif text-3xl mt-10 mb-4">Cuándo considerar suplementación dirigida</h2>
        <p className="text-[#1a2421] leading-relaxed mb-8">
          Si tienes 3+ resfriados al año, fatiga persistente, alergias o estás en una etapa de alta demanda (oposiciones, deporte intenso, embarazo, lactancia, post-COVID), una suplementación dirigida puede acelerar dramáticamente la mejora. <a href="/#quiz" className="text-[#bc6c25] font-semibold underline underline-offset-4">Haz nuestro quiz wellness gratuito</a> y recibe un protocolo personalizado de 3 meses.
        </p>

        <h2 className="font-serif text-3xl mt-10 mb-4">Aviso médico</h2>
        <p className="text-sm text-[#5c6b61] leading-relaxed italic">
          Esta guía es educativa, no sustituye consulta médica. Si tienes síntomas persistentes, enfermedad crónica, tomas medicamentos o estás embarazada/lactando, consulta con un profesional de la salud antes de implementar cambios significativos o tomar suplementos.
        </p>
      </PillarPageLayout>
    </>
  );
}
